import { gql, useQuery } from '@apollo/client';
import { css } from '@emotion/core';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import FormControl from '@material-ui/core/FormControl';
import IconButton from '@material-ui/core/IconButton';
import TextField from '@material-ui/core/TextField';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import CloseIcon from '@material-ui/icons/Close';
import EmailIcon from '@material-ui/icons/Email';
import ErrorIcon from '@material-ui/icons/Error';
import HelpIcon from '@material-ui/icons/Help';
import axios from 'axios';
import { DateTime } from 'luxon';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useHybridAuthToken } from '../components/hybridAuthToken';
import { INVOICE_SERVICE_CREATION_DATE } from '../constants/invoices';
import useEnvConfig from '../hooks/useEnvConfig';
import formatCurrency from '../utils/format-currency';
import { formatDateField } from '../utils/helpers';
import mixpanel from '../utils/mixpanel';
import { validateEmail } from '../utils/validations';

const CONTACT_EMAIL_QUERY = gql`
  query ContactEmailQuery($contactId: ID!) {
    contact(id: $contactId) {
      email
    }
  }
`;

const EmailInvoiceButton = ({ small, invoiceDate, invoiceKey, contactId, prospectId, amount, comments }) => {
  const [emailSent, setEmailSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [emailAddress, setEmailAddress] = useState('');
  const [isEmailValid, setIsEmailValid] = useState(true);

  const { loading: queryLoading, data } = useQuery(CONTACT_EMAIL_QUERY, { variables: { contactId } });

  const { paymentServiceAPIUrl } = useEnvConfig();
  const hybridAuth = useHybridAuthToken();

  const invoiceMigrationDate = DateTime.fromISO(INVOICE_SERVICE_CREATION_DATE);
  const isInvoiceDatePostMigration = DateTime.fromISO(invoiceDate, { setZone: true }) > invoiceMigrationDate;

  const customerEmail = data?.contact?.email;
  const formattedInvoiceDate = formatDateField(invoiceDate);
  const formattedAmount = amount != null ? formatCurrency(amount) : null;

  // Populate email when query data loads
  useEffect(() => {
    if (customerEmail && !emailAddress) {
      setEmailAddress(customerEmail);
      setIsEmailValid(true);
    }
  }, [customerEmail, emailAddress]);

  const handleClick = () => {
    // Reset email to customer's email when opening modal
    if (customerEmail) {
      setEmailAddress(customerEmail);
      setIsEmailValid(true);
    }
    setError(false);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    if (!loading) {
      setIsModalOpen(false);
      setError(false);
      // Reset email to customer's email when closing
      if (customerEmail) {
        setEmailAddress(customerEmail);
        setIsEmailValid(true);
      }
    }
  };

  const handleEmailChange = (e) => {
    const newEmail = e.target.value;
    setEmailAddress(newEmail);
    setIsEmailValid(validateEmail(newEmail));
  };

  const handleSendEmail = async () => {
    if (!hybridAuth || !isEmailValid || !emailAddress) {
      return;
    }

    setLoading(true);
    setError(false);

    try {
      const response = await axios.post(
        `${paymentServiceAPIUrl}/email-invoice/${invoiceKey}`,
        { customerEmail: emailAddress },
        {
          headers: { Authorization: hybridAuth },
          params: {
            prospectId: prospectId,
            contactId: contactId,
          },
        },
      );

      if (response.status === 200) {
        setEmailSent(true);
        setLoading(false);
        setIsModalOpen(false);
        mixpanel.track('Email Invoice', {
          'Invoice Key': invoiceKey,
          Type: small ? 'Historical' : 'Current',
        });
        // Reset the success state after 3 seconds
        setTimeout(() => setEmailSent(false), 3000);
      } else {
        setError(true);
        setLoading(false);
        mixpanel.track('Email Invoice', {
          'Invoice Key': invoiceKey,
          Type: !isInvoiceDatePostMigration ? 'Invoice created before migration' : 'Error',
        });
      }
    } catch (httpRequestError) {
      setError(true);
      setLoading(false);
      mixpanel.track('Email Invoice', {
        'Invoice Key': invoiceKey,
        Type: !isInvoiceDatePostMigration ? 'Invoice created before migration' : 'Error',
      });
    }
  };

  if (!invoiceKey) {
    return (
      <Tooltip title="Your invoice will be available 10 days prior to your due date.">
        <HelpIcon color="disabled" fontSize="small" />
      </Tooltip>
    );
  }

  if (small) {
    if (queryLoading) {
      return (
        <Tooltip title="Loading...">
          <CircularProgress size={20} />
        </Tooltip>
      );
    }

    if (loading) {
      return (
        <Tooltip title="Sending invoice...">
          <CircularProgress size={20} />
        </Tooltip>
      );
    }

    if (error) {
      return (
        <Tooltip title="Error sending invoice">
          <ErrorIcon color="error" fontSize="small" />
        </Tooltip>
      );
    }

    if (!isInvoiceDatePostMigration) {
      return (
        <Tooltip title="Invoice is no longer accessible">
          <HelpIcon color="disabled" fontSize="small" />
        </Tooltip>
      );
    }

    if (!customerEmail) {
      return (
        <Tooltip title="No email found for customer">
          <ErrorIcon color="error" fontSize="small" />
        </Tooltip>
      );
    }

    if (emailSent) {
      return (
        <Tooltip title="Invoice sent successfully">
          <EmailIcon color="primary" fontSize="small" />
        </Tooltip>
      );
    }

    return (
      <>
        <Tooltip title="Email invoice">
          <IconButton size="small" aria-label="Email invoice" onClick={handleClick} disabled={queryLoading}>
            <EmailIcon color="primary" fontSize="small" />
          </IconButton>
        </Tooltip>
        <Dialog
          open={isModalOpen}
          onClose={handleCloseModal}
          aria-labelledby="email-invoice-dialog-title"
          disableBackdropClick={loading}
          disableEscapeKeyDown={loading}
        >
          <DialogTitle disableTypography>
            <Typography variant="h6">Email Invoice</Typography>
            <IconButton
              aria-label="close"
              onClick={handleCloseModal}
              css={css`
                position: absolute;
                right: 8px;
                top: 8px;
                color: #9e9e9e;
              `}
              disabled={loading}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent
            css={css`
              min-width: 400px;
              padding: 16px;
            `}
            dividers
          >
            {loading && (
              <div
                css={css`
                  display: flex;
                  justify-content: center;
                  margin-bottom: 16px;
                `}
              >
                <CircularProgress />
              </div>
            )}
            {error && (
              <DialogContentText
                color="error"
                css={css`
                  margin-bottom: 16px;
                `}
              >
                Something went wrong. Please try again.
              </DialogContentText>
            )}
            {(formattedInvoiceDate || comments) && (
              <div
                css={css`
                  margin-bottom: 20px;
                `}
              >
                <Typography
                  variant="subtitle1"
                  css={css`
                    margin-bottom: 8px;
                    font-weight: 500;
                  `}
                >
                  {comments && formattedInvoiceDate
                    ? `${comments} - ${formattedInvoiceDate}`
                    : comments || formattedInvoiceDate}
                  {invoiceKey && (
                    <Typography
                      component="span"
                      variant="body2"
                      css={css`
                        color: #757575;
                        font-weight: 400;
                        margin-left: 8px;
                      `}
                    >
                      ({invoiceKey})
                    </Typography>
                  )}
                </Typography>
                {formattedAmount && (
                  <Typography
                    variant="body1"
                    css={css`
                      margin-bottom: 8px;
                      font-weight: 500;
                      color: #1976d2;
                    `}
                  >
                    Amount: {formattedAmount}
                  </Typography>
                )}
              </div>
            )}
            {customerEmail && (
              <DialogContentText
                color="textSecondary"
                css={css`
                  margin-bottom: 16px;
                `}
              >
                The customer&apos;s email on file is {customerEmail}
              </DialogContentText>
            )}
            <FormControl fullWidth>
              <TextField
                label="Email Address"
                variant="outlined"
                value={emailAddress}
                onChange={handleEmailChange}
                placeholder="Enter email address"
                error={!isEmailValid && emailAddress.length > 0}
                helperText={!isEmailValid && emailAddress.length > 0 ? 'Please enter a valid email address' : ''}
                disabled={loading}
                required
                fullWidth
                css={css`
                  margin-bottom: 8px;
                `}
              />
            </FormControl>
            <DialogContentText
              color="textSecondary"
              css={css`
                font-size: 0.875rem;
              `}
            >
              The invoice will be sent to this email address.
            </DialogContentText>
          </DialogContent>
          <DialogActions
            css={css`
              margin: 0;
              padding: 8px;
            `}
          >
            <Button onClick={handleCloseModal} color="primary" disabled={loading}>
              Cancel
            </Button>
            <Button
              onClick={handleSendEmail}
              color="primary"
              variant="contained"
              disabled={loading || !isEmailValid || !emailAddress}
              autoFocus
            >
              {loading ? 'Sending...' : 'Send'}
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  } else {
    if (error) {
      return <Typography color="error">Unable to send invoice</Typography>;
    }

    if (!isInvoiceDatePostMigration) {
      return <Typography>Invoice is no longer accessible</Typography>;
    }

    if (!customerEmail) {
      return <Typography color="error">No email found for customer</Typography>;
    }

    if (emailSent) {
      return (
        <Button startIcon={<EmailIcon />} variant="text" color="primary" disabled>
          Invoice Sent
        </Button>
      );
    }

    return (
      <>
        <Button
          onClick={handleClick}
          startIcon={loading || queryLoading ? <CircularProgress size={20} /> : <EmailIcon />}
          variant="text"
          color="primary"
          disabled={loading || queryLoading}
        >
          Email Invoice
        </Button>
        <Dialog
          open={isModalOpen}
          onClose={handleCloseModal}
          aria-labelledby="email-invoice-dialog-title"
          disableBackdropClick={loading}
          disableEscapeKeyDown={loading}
        >
          <DialogTitle disableTypography>
            <Typography variant="h6">Email Invoice</Typography>
            <IconButton
              aria-label="close"
              onClick={handleCloseModal}
              css={css`
                position: absolute;
                right: 8px;
                top: 8px;
                color: #9e9e9e;
              `}
              disabled={loading}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent
            css={css`
              min-width: 400px;
              padding: 16px;
            `}
            dividers
          >
            {loading && (
              <div
                css={css`
                  display: flex;
                  justify-content: center;
                  margin-bottom: 16px;
                `}
              >
                <CircularProgress />
              </div>
            )}
            {error && (
              <DialogContentText
                color="error"
                css={css`
                  margin-bottom: 16px;
                `}
              >
                Something went wrong. Please try again.
              </DialogContentText>
            )}
            {(formattedInvoiceDate || comments) && (
              <div
                css={css`
                  margin-bottom: 20px;
                `}
              >
                <Typography
                  variant="subtitle1"
                  css={css`
                    margin-bottom: 8px;
                    font-weight: 500;
                  `}
                >
                  {comments && formattedInvoiceDate
                    ? `${comments} - ${formattedInvoiceDate}`
                    : comments || formattedInvoiceDate}
                  {invoiceKey && (
                    <Typography
                      component="span"
                      variant="body2"
                      css={css`
                        color: #757575;
                        font-weight: 400;
                        margin-left: 8px;
                      `}
                    >
                      ({invoiceKey})
                    </Typography>
                  )}
                </Typography>
                {formattedAmount && (
                  <Typography
                    variant="body1"
                    css={css`
                      margin-bottom: 8px;
                      font-weight: 500;
                      color: #1976d2;
                    `}
                  >
                    Amount: {formattedAmount}
                  </Typography>
                )}
              </div>
            )}
            {customerEmail && (
              <DialogContentText
                color="textSecondary"
                css={css`
                  margin-bottom: 16px;
                `}
              >
                The customer&apos;s email on file is {customerEmail}
              </DialogContentText>
            )}
            <FormControl fullWidth>
              <TextField
                label="Email Address"
                variant="outlined"
                value={emailAddress}
                onChange={handleEmailChange}
                placeholder="Enter email address"
                error={!isEmailValid && emailAddress.length > 0}
                helperText={!isEmailValid && emailAddress.length > 0 ? 'Please enter a valid email address' : ''}
                disabled={loading}
                required
                fullWidth
                css={css`
                  margin-bottom: 8px;
                `}
              />
            </FormControl>
            <DialogContentText
              color="textSecondary"
              css={css`
                font-size: 0.875rem;
              `}
            >
              The invoice will be sent to this email address.
            </DialogContentText>
          </DialogContent>
          <DialogActions
            css={css`
              margin: 0;
              padding: 8px;
            `}
          >
            <Button onClick={handleCloseModal} color="primary" disabled={loading}>
              Cancel
            </Button>
            <Button
              onClick={handleSendEmail}
              color="primary"
              variant="contained"
              disabled={loading || !isEmailValid || !emailAddress}
              autoFocus
            >
              {loading ? 'Sending...' : 'Send'}
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }
};

EmailInvoiceButton.propTypes = {
  small: PropTypes.bool.isRequired,
  invoiceDate: PropTypes.string.isRequired,
  invoiceKey: PropTypes.string,
  contactId: PropTypes.string.isRequired,
  prospectId: PropTypes.string.isRequired,
  amount: PropTypes.number,
  comments: PropTypes.string,
};

EmailInvoiceButton.defaultProps = {
  invoiceKey: null,
};

export default EmailInvoiceButton;
